import React, { memo } from 'react';
import { ProductCard } from './ProductCard';
import { CheckCircle } from 'lucide-react';
export function RankingSection() {
  const products = [{
    rank: 1,
    brandName: 'Faith & Fig 4:14 Saffron',
    tagline: 'Clinical-grade Potency and Dosing, Best Overall for AD*D Regulation',
    rating: 9.8,
    grade: 'A+',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/36128008-8b6f-473a-bed4-a1d7911e8970.png?v=1764132161',
    summary: 'From the very first week of research, Faith & Fig\'s saffron supplement left the others in the dust. Women consistently reported calmer mornings, steadier reactions throughout the day, better follow-through on tasks, and - most importantly - being able to stay present during prayer and with their families. And the best part? These results weren\'t just a temporary "honeymoon phase." The effects stayed steady week after week - without the emotional blunting many women experience on stimulant medications.',
    keyStrengths: [<>
          Clinically correct dosing — 30mg of Safr'Inside standardized saffron extract, the exact amount used in clinical trials showing results comparable to Ritalin, without stimulant side effects.
        </>, 'Targets the full calm-regulate-focus loop — Saffron\'s three active compounds (crocin, safranal, crocetin) work together to support emotional balance, mental clarity, and parasympathetic nervous system activation (your body\'s natural "calm down" system)', "Biblical sourcing — Saffron is mentioned in Song of Solomon 4:14 as a precious spice, and Faith & Fig specifically positions this as using God's creation for stewardship of your mind", 'Research-backed for AD*D — Multiple clinical studies show saffron improves working memory, attention, emotional balance, anxiety symptoms, and sleep quality', 'No emotional blunting — Unlike stimulants that can make you feel like a "zombie," women report feeling more like themselves — just calmer and more focused', 'Created specifically for Christians — The only AD*D supplement designed with your faith and values in mind.'],
    ratingsGrid: {
      effectiveness: 9.9,
      formulaQuality: 9.8,
      value: 9.7,
      customerSatisfaction: 9.8,
      returnPolicy: 9.7
    },
    pros: ['Only supplement with clinically-dosed saffron (30mg) specifically for the calm-focus-regulate cycle', 'Backed by studies showing effectiveness comparable to Ritalin, but without stimulant side effects', 'Faith-aligned brand that addresses spiritual concerns about using supplements', 'Single-ingredient purity (no "kitchen sink" blends with unknown interactions)', 'Clinical formula safe and effective for all ages'],
    cons: ["Only available online through Faith & Fig's website", 'Has sold out multiple times due to word-of-mouth popularity in Christian mom groups'],
    testerQuote: 'Women testing this supplement reported being able to sit through their child\'s entire bedtime story without their mind wandering, staying calm when the morning routine fell apart, and - for the first time in years - being able to pray without their thoughts spinning in ten different directions. Several described it as "finally feeling like the mom God created me to be."',
    isTopPick: true
  }, {
    rank: 2,
    brandName: 'BrainMD Happy Saffron Plus',
    tagline: 'High-Quality Ingredients, But Under-Dosed',
    rating: 8.5,
    grade: 'B+',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_242_happy-saffron-plus-bottle-front-saffron-supplement-brainmd.png?v=1764567855',
    summary: "BrainMD uses high-quality ingredients (Affron® saffron extract + Longvida® curcumin + zinc) and has name recognition from Dr. Daniel Amen's reputation. But the saffron is under-dosed compared to the clinical benchmark of 30mg. It's also formulated for general mood support, not specifically for distractibility or emotional dysregulation.",
    keyStrengths: ['High-quality branded ingredients', "Dr. Daniel Amen's reputation", 'Good for general mood support'],
    ratingsGrid: {
      effectiveness: 8.0,
      formulaQuality: 8.5,
      value: 8.5,
      customerSatisfaction: 8.0,
      returnPolicy: 8.0
    },
    pros: ['Reputable brand with quality ingredients', 'Good for mild mood support'],
    cons: ['Curcumin can cause digestive discomfort in sensitive women', 'Not designed for the calm-focus-regulate cycle', "Generic wellness branding (doesn't reflect Christian values)"]
  }, {
    rank: 3,
    brandName: 'Thesis "Clarity"',
    tagline: 'Too Stimulant-Heavy for Sensitive Women',
    rating: 7.4,
    grade: 'C+',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/324b8ece-c798-480c-96ce-b77978bc06fb.png?v=1764567767',
    summary: 'Thesis markets itself as a personalized alternative to prescription stimulants and has gained traction among biohackers and high-performing professionals. The problem? Its formula uses high-stimulation nootropics (CDP-Choline, Panax ginseng) that many Christian women report cause jitteriness, overstimulation, and increased anxiety.',
    keyStrengths: ['Popular among biohackers', 'Personalized approach'],
    ratingsGrid: {
      effectiveness: 7.5,
      formulaQuality: 7.0,
      value: 7.0,
      customerSatisfaction: 7.5,
      returnPolicy: 7.5
    },
    pros: ['May work for high-performing professionals'],
    cons: ['Too stimulant-adjacent for emotionally sensitive women', 'Multiple ingredients with no clinical trials', 'Many women report feeling "wired but not calm" - the opposite of what overwhelmed moms need', 'Zero spiritual alignment or acknowledgment of faith-based concerns']
  }, {
    rank: 4,
    brandName: 'Noocube Brain Productivity',
    tagline: 'Kitchen Sink Formula with Unpredictable Results',
    rating: 7.0,
    grade: 'C',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_51_1-bottle_8f9cb220-d68b-40de-b227-bf4be539db03.png?v=1764579247',
    summary: 'Noocube offers a broad "kitchen sink" formula with Lutemax® 2020, Bacopa, L-theanine, cat\'s claw, B-vitamins, and tyrosine. However, there are zero clinical trials on AD*D outcomes.',
    keyStrengths: ['Multiple ingredients', 'General brain health focus'],
    ratingsGrid: {
      effectiveness: 7.0,
      formulaQuality: 6.5,
      value: 7.0,
      customerSatisfaction: 7.0,
      returnPolicy: 7.0
    },
    pros: ['May support general brain health'],
    cons: ['Overloaded with ingredients (unpredictable synergy and potential side effects)', 'Many users report digestive upset, headaches, and afternoon fatigue crashes', 'Generic nootropic branding with zero spiritual alignment', 'No focus on emotional regulation']
  }, {
    rank: 5,
    brandName: 'Onnit Alpha Brain',
    tagline: 'Famous Marketing, Problematic Side Effects',
    rating: 6.5,
    grade: 'C-',
    imageUrl: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_202_media_0d26030c-9e9b-40bd-82ae-df059b2ad547.png?v=1764568019',
    summary: 'Onnit is famous for its marketing and celebrity endorsements, but fame isn\'t efficacy. Major issues: Contains Huperzine A, which is known for causing nightmares, head pressure, irritability, overstimulation, and sleep disruption in many women. Not clinically validated. Uses proprietary blends (you can\'t verify actual ingredient doses). Many women report feeling "wired," "dizzy," or "emotionally flat."',
    keyStrengths: ['Well-known brand', 'Celebrity endorsements'],
    ratingsGrid: {
      effectiveness: 6.0,
      formulaQuality: 6.5,
      value: 6.5,
      customerSatisfaction: 6.0,
      returnPolicy: 6.5
    },
    pros: ['Widely available'],
    cons: ['Contains Huperzine A (known for causing nightmares and sleep disruption)']
  }];
  return <div className="bg-[#FAF8F5] py-16" data-id="element-194">
      <div className="editorial-wide" data-id="element-195">
        {/* How We Ranked Section */}
        <div className="mb-16" data-id="element-196">
          <h2 className="text-3xl font-bold text-[#1A2332] mb-6" data-id="element-197">
            How We Ranked These Supplements
          </h2>
          <div className="bg-white p-8 rounded-xl border border-[#E8E4DD]" data-id="element-198">
            <ul className="grid md:grid-cols-2 gap-4 mb-6" data-id="element-199">
              {[<>
                  <b data-id="element-200">Clinical Dosing</b>: Does it contain the research-backed
                  amount of active ingredients? Or is it under-dosed to save
                  money?
                </>, <>
                  <b data-id="element-201">Full-Cycle Support</b>: Does it address calm + focus +
                  emotional regulation together? Or does it only target one
                  piece, leaving you still struggling with the others?
                </>, <>
                  <b data-id="element-202">Transparency & Quality</b>: Are ingredients clearly listed,
                  clinically studied, and manufactured to consistent standards?
                </>, <>
                  <b data-id="element-203">Faith Alignment</b>: Does the brand understand Christian
                  women's concerns about using supplements?
                </>, <>
                  <b data-id="element-204">User Feedback</b>: Do real Christian women consistently
                  report meaningful improvements in focus, calm, and emotional
                  steadiness?
                </>].map((criterion, index) => <li key={index} className="flex items-start gap-3 text-[#1A2332]/80" data-id="element-205">
                  <CheckCircle className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" data-id="element-206" />
                  <span data-id="element-207">{criterion}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Section Title */}
        <h2 className="text-3xl font-bold text-[#1A2332] mb-8" data-id="element-208">
          Our Top 5 Mood & Focus Supplements
        </h2>

        {/* Product Rankings */}
        <div className="space-y-8 mb-16" data-id="element-209">
          {products.map(product => <ProductCard key={product.rank} {...product} data-id="element-210" />)}
        </div>
      </div>
    </div>;
}